import React, { useState, useMemo } from "react";
import PageLoader from "../components/ui/PageLoader";
import "../assets/css/style.css";
import { FiEdit2, FiTrash2 } from "react-icons/fi";
import Modal from "../components/ui/Modal";
import {
  useAddTemplateMutation,
  useDeleteTemplateMutation,
  useGetAllTemplatesQuery,
  useUpdateTemplateMutation,
} from "apis/commsTemplateVendor";
import { FaPlus } from "react-icons/fa6";
import TemplateEdit from "components/templates/edit";
import TemplateView from "components/templates/view";
import Button from "components/ui/Button";
import Table from "components/ui/Table";
import { useGetNetworksQuery } from "apis/network";

const CommsTemplateVendor = () => {
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  // Table page size
  const pageSize = 10;

  const { mutate: updateTemplate, isLoading: isTemplateLoading } =
    useUpdateTemplateMutation();

  const {
    templates = [],
    tags = [],
    isPending: isTemplatesPending,
  } = useGetAllTemplatesQuery();

  const { data: networks } = useGetNetworksQuery();

  const { mutate: addTemplate } = useAddTemplateMutation();
  const { mutate: deleteTemplate } = useDeleteTemplateMutation();

  const columns = useMemo(
    () => [
      {
        accessorKey: "index",
        header: "#",
        cell: (_, rowIndex, currentPage) =>
          (currentPage - 1) * pageSize + rowIndex + 1,
        isFilter: false,
        isSortable: false,
      },
      {
        accessorKey: "name",
        header: "Name",
        isFilter: true,
        minWidth: "180px",
      },
      {
        accessorKey: "tags",
        header: "Tags",
        cell: (row) => {
          const templateTags = row.tags;
          if (!templateTags || templateTags.length === 0) {
            return "No tags";
          }
          return (
            <div className="flex flex-wrap gap-1">
              {templateTags.map((tag) => (
                <span
                  key={tag._id}
                  className="bg-gray-100 text-gray-800 px-2 py-1 rounded text-xs"
                >
                  {tag.name}
                </span>
              ))}
            </div>
          );
        },
        isFilter: false,
        isSortable: false,
      },
      {
        accessorKey: "approval",
        header: "Approval",
        cell: (row) => {
          const isApproved = row.approval || false;
          return (
            <span
              className={`font-medium ${isApproved ? "text-green-600" : "text-red-600"}`}
            >
              {isApproved ? "Yes" : "No"}
            </span>
          );
        },
        isFilter: false,
        isSortable: false,
      },
      {
        accessorKey: "networkId",
        header: "Network",
        cell: (row) => <span>{row?.networkId?.name || "All networks"}</span>,
        isFilter: false,
        isSortable: false,
      },
      {
        accessorKey: "actions",
        header: "Actions",
        cell: (row) => (
          <div className="flex items-center space-x-4">
            <button
              onClick={() => {
                setSelectedId(row._id);
                setIsEditOpen(true);
              }}
              className="edit-icon"
              title="Edit Template"
            >
              <FiEdit2 fontSize={20} />
            </button>
            <button
              onClick={() => {
                setSelectedId(row._id);
                setIsDeleteOpen(true);
              }}
              className="delete-icon"
              title="Delete Template"
            >
              <FiTrash2 fontSize={20} />
            </button>
          </div>
        ),
        isFilter: false,
        isSortable: false,
        stopPropagation: true,
        minWidth: "100px",
      },
    ],
    [],
  );

  if (isTemplateLoading || isTemplatesPending) {
    return <PageLoader />;
  }

  const selectedTemplate = templates.find((t) => t._id === selectedId);

  const handleDelete = () => {
    deleteTemplate(selectedId);
    setIsDeleteOpen(false);
  };

  const handleViewRowClick = (row) => {
    setSelectedId(row._id);
    setIsViewOpen(true);
  };

  const AddTemplateButton = () => (
    <button
      className="black-button flex items-center"
      onClick={() => setIsAddOpen(true)}
    >
      <FaPlus size={14} className="mr-2" />
      Add Template
    </button>
  );

  return (
    <>
      {/* Edit Modal */}
      <Modal
        isOpen={isEditOpen}
        onClose={() => setIsEditOpen(false)}
        title="Template Edit"
        className="max-w-2xl"
      >
        <TemplateEdit
          initialData={selectedTemplate}
          tags={tags}
          networks={networks}
          showAgentProTipDraft
          showWhenItGoesOut
          onSubmit={(data) => {
            updateTemplate({ id: selectedId, ...data });
            setIsEditOpen(false);
          }}
        />
      </Modal>

      {/* Add Modal */}
      <Modal
        isOpen={isAddOpen}
        onClose={() => setIsAddOpen(false)}
        title="Template Add"
        className="max-w-2xl"
      >
        <TemplateEdit
          initialData=""
          tags={tags}
          networks={networks}
          showAgentProTipDraft
          showWhenItGoesOut
          onSubmit={(data) => {
            addTemplate(data);
            setIsAddOpen(false);
          }}
        />
      </Modal>

      {/* Delete Modal */}
      <Modal
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        title="Delete Template"
      >
        <div>
          Are you sure you want to delete this Template?
          <div className="w-full flex gap-2 justify-end py-4">
            <Button
              onClick={() => setIsDeleteOpen(false)}
              className="gray-button"
            >
              No
            </Button>
            <Button onClick={handleDelete} className="black-button">
              Yes
            </Button>
          </div>
        </div>
      </Modal>

      {/* View Modal */}
      <Modal
        isOpen={isViewOpen}
        onClose={() => setIsViewOpen(false)}
        title="Template Details"
        className="max-w-2xl"
      >
        <TemplateView
          data={selectedTemplate}
          showAgentProTipDraft
          showWhenItGoesOut
          showNetwork
        />
      </Modal>

      {/* Table */}
      <div className="container mx-auto">
        <Table
          title="Vendor Templates"
          columns={columns}
          data={templates}
          pageSize={pageSize}
          ButtonCmp={AddTemplateButton}
          onRowClick={handleViewRowClick}
        />
      </div>
    </>
  );
};

export default CommsTemplateVendor;
